import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  createContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { Button, Icon, Modal, Dropdown } from 'semantic-ui-react';
import SearchReplaceControls from './searchReplaceControls';
import EditDialog from './editDialog';
import ListItem from './jsonListItem';
import {
  correctMissingUniqueKeys,
  countUniqueValues,
  initialData,
  searchOptions,
  setEditedStoryJson,
  // selectEditedStoryJson,
  viewOptions,
} from '../../slices/storyEditorSlice';
import { useVirtualizer } from '@tanstack/react-virtual';
import './jsonEditor.css';

const ListContext = createContext(null);

const JsonStoryEditor = ({ storyJsonData, uuid, onUpdate }) => {
  const dispatch = useDispatch();
  const { dialogueArray } = storyJsonData.dnObj;

  // Local state management
  const [nodes, setNodes] = useState(initialData.dnObj.dialogueArray);
  const [filteredNodes, setFilteredNodes] = useState(initialData.dnObj.dialogueArray);
  const [editKey, setEditKey] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [editNewNode, setEditNewNode] = useState(false);
  const [droppedRow, setDroppedRow] = useState(null);
  const [view, setView] = useState('editor');
  const [overScanCount, setOverscanCount] = useState(20);
  const nodeListRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Virtualizer setup
  const rowVirtualizer = useVirtualizer({
    count: filteredNodes.length,
    getScrollElement: () => nodeListRef.current,
    estimateSize: () => 42,
    overscan: overScanCount, // filteredNodes.length,
    getItemKey: (index) => filteredNodes[index].uniqueKey,
  });

  // Sync storyJsonData with local state
  const updateNodesWithCorrectedKeys = (array) => {
    if (!array?.length) return [];

    const updatedNodes = correctMissingUniqueKeys(array);
    setNodes(updatedNodes);
    setFilteredNodes(updatedNodes);

    return updatedNodes;
  };
  useEffect(() => {
    updateNodesWithCorrectedKeys(dialogueArray);
  }, [dialogueArray])

  // Re-measure virtual items
  useEffect(() => {
    rowVirtualizer.getVirtualItems().forEach((virtualItem, i) => {
      const element = document.querySelector(`[data-index="${i}"]`);
      if (element) {
        const newHeight = element.getBoundingClientRect().height;
        rowVirtualizer.resizeItem(i, newHeight);
      }
    });
  }, [view, rowVirtualizer, nodes.length]);

  // Handlers for searching and replacing content
  const handleSearch = useCallback(
    (query, replaceQuery, searchItem) => {
      setSearchQuery(query);  // save the query for highlighting

      const lowerQuery = query.toLowerCase();

      let filtered = nodes.filter((node) => {
        const type = node.type?.toLowerCase() || '';
        const speaker = node.speaker?.toLowerCase() || '';
        const emotion = node.emotion?.toLowerCase() || '';
        const content = node.content?.toLowerCase() || '';

        switch (searchItem) {
          case 'type': return type.includes(lowerQuery);
          case 'speaker': return speaker.includes(lowerQuery);
          case 'emotion': return emotion.includes(lowerQuery);
          case 'content': return content.includes(lowerQuery);
          case 'all':
          default:
            return type.includes(lowerQuery) ||
              speaker.includes(lowerQuery) ||
              emotion.includes(lowerQuery) ||
              content.includes(lowerQuery);
        }
      });

      if (replaceQuery) {
        console.log('searchItem', searchItem)
        filtered = filtered.map((node) => ({
          ...node,
          type: searchItem === 'type'
            ? node.type?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.type
            : node.type,
          speaker: searchItem === 'speaker'
            ? node.speaker?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.speaker
            : node.speaker,
          emotion: searchItem === 'emotion'
            ? node.emotion?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.emotion
            : node.emotion,
          content: searchItem === 'content'
            ? node.content?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.content
            : node.content,
        }));

        // If searchItem is "all", handle it separately to replace across all fields
        if (searchItem === 'all') {
          filtered = filtered.map((node) => ({
            ...node,
            type: node.type?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.type,
            speaker: node.speaker?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.speaker,
            emotion: node.emotion?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.emotion,
            content: node.content?.replace(new RegExp(lowerQuery, 'gi'), replaceQuery) || node.content,
          }));
        }

        console.log('filtered and replaced', filtered)
      }
      setNodes(filtered);
      setFilteredNodes(filtered);
    },
    [nodes, setNodes, setFilteredNodes]
  );

  const handleClearFilter = useCallback(() => {
    updateNodesWithCorrectedKeys(dialogueArray);
    setSearchQuery('');  // This resets the query and clears any highlighting
  }, [dialogueArray, updateNodesWithCorrectedKeys]);

  // Handlers for editing nodes
  const handleUpdate = (updatedNode) => {
    const index = filteredNodes.findIndex((node) => node.uniqueKey === editKey);
    if (index !== -1) {
      const newNodes = [...filteredNodes];
      newNodes[index] = updatedNode;
      setFilteredNodes(newNodes);
      setNodes(newNodes);
      dispatch(setEditedStoryJson({ activeStoryJson: newNodes, uuid }));
    }
    setEditKey(null);
    setIsEditEnabled(false);
    setEditNewNode(false);
    onUpdate(true);
  };

  const handleMove = useCallback(
    ({ startIndex, indexOfTarget }) => {
      if (indexOfTarget < 0 || indexOfTarget >= filteredNodes.length) return;
      const newNodes = [...filteredNodes];
      const [movedNode] = newNodes.splice(startIndex, 1);
      newNodes.splice(indexOfTarget, 0, movedNode);
      setNodes(newNodes);
      setFilteredNodes(newNodes);
      setDroppedRow(indexOfTarget);
      setEditKey(newNodes[indexOfTarget].uniqueKey);
      onUpdate(true);
    },
    [filteredNodes, onUpdate]
  );

  const handleRowClick = useCallback(
    (node, index) => {
      if (index < 0 || index >= filteredNodes.length) return;
      setDroppedRow(index);
      console.log('handleRowClick', node)
      setEditKey(node.uniqueKey);
      onUpdate(true);
    },
    [filteredNodes, onUpdate]
  );

  const handleEdit = useCallback((index) => {
    setEditKey(filteredNodes[index].uniqueKey);
    setIsEditEnabled(true);
    setDroppedRow(index);
  }, [filteredNodes]);

  // Handlers for deleting nodes
  const handleDelete = (index) => {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    const newNodes = nodes.filter((_, i) => i !== deleteIndex);
    setNodes(newNodes);
    setFilteredNodes(newNodes);
    setIsDeleteModalOpen(false);
  };

  // Handlers for canceling editing
  const handleCancel = () => {
    setEditKey(null);
    setIsEditEnabled(false);
    setEditNewNode(false);
  };

  // Scroll to top of list
  const scrollToStart = () => {
    if (nodeListRef.current) {
      // Scroll to the very start of the list (offset 0)
      rowVirtualizer.scrollToIndex(0, { behavior: 'smooth', align: 'start' });
    }
  };

  const scrollToEnd = useCallback(() => {
    if (nodeListRef.current) {
      // Increase overscan to force rendering of all items.
      setOverscanCount(filteredNodes.length);

      // Let the virtualizer update before scrolling.
      setTimeout(() => {
        const containerHeight = nodeListRef.current.clientHeight;
        const totalSize = rowVirtualizer.getTotalSize();
        const offset = totalSize - containerHeight;
        rowVirtualizer.scrollToOffset(offset, { behavior: 'smooth' });

        // Check if the last item (with index filteredNodes.length - 1) is rendered.
        const virtualItems = rowVirtualizer.getVirtualItems();
        const lastIndex = filteredNodes.length - 1;
        const isLastItemRendered = virtualItems.some(item => item.index === lastIndex);

        if (!isLastItemRendered) {
          // If not rendered, try scrolling again after a short delay.
          setTimeout(() => {
            scrollToEnd();
          }, 100);
        } else {
          // Once the last item is rendered, restore the default overscan.
          setOverscanCount(20);
        }
      }, 0);
    }
  }, [filteredNodes, rowVirtualizer]);

  const renderFileStats = useMemo(() => {
    const { types, emotions, speakers } = countUniqueValues(nodes);

    // Actual speakers (characters) — what "Character Count" should reflect.
    const actors = (speakers || []).map(({ speaker, count }) => ({
      key: speaker,
      text: `${speaker} - ${count}`,
      value: speaker,
    }));

    // Categories (dialogue / narration / bookPart / chapter / ...) kept for reference.
    const categories = types.map(({ type, count }) => ({
      key: type,
      text: `${type} - ${count}`,
      value: type,
    }));

    const emotes = emotions.map(({ emotion, count }) => ({
      key: emotion,
      text: `${emotion} - ${count}`,
      value: emotion,
    }));

    // Total dialogue rows (rows that have a non-empty speaker).
    const dialogCount = (speakers || []).reduce((acc, s) => acc + s.count, 0);

    return (
      <div>
        <div className="storyStatsHeader">Story Stats</div>
        <dl className="inline-dl">
          <dt className="file-stats-dt">Row Count:</dt>
          <dd className="file-stats-dt">{nodes.length}</dd>
          <dt className="file-stats-dt">Dialog Count:</dt>
          <dd className="file-stats-dt">{dialogCount}</dd>
          <dt className="file-stats-dt">Character Count:</dt>
          <dd className="file-stats-dt">{actors.length}</dd>
          <dt className="file-stats-dt">Emotion Count:</dt>
          <dd className="file-stats-dt">{emotes.length}</dd>
        </dl>
        <Dropdown className="fe-stats-dropdown" inline text="Actors" options={actors} />
        <Dropdown className="fe-stats-dropdown" inline text="Categories" options={categories} style={{ marginLeft: '15px' }} />
        <Dropdown className="fe-stats-dropdown" inline text="Emotions" options={emotes} style={{ marginLeft: '15px' }} />
      </div>
    );
  }, [nodes]);

  // Renders header based on current view mode (editor or reader)
  const renderHeader = () => {
    if (view === 'editor') {
      return (
        <div className="fixed-header">
          <div className="header-field">Speaker</div>
          {/* <div className="header-field">Unique Key</div> */}
          <div className="header-field">Category</div>
          <div className="header-field">Emotion</div>
          <div className="header-field">Content</div>
          <div className={filteredNodes.length > 17 ? 'action-field' : 'header-field'}>
            <span style={{ display: "inline", height: '100%', marginTop: '4px', marginLeft: "-64px" }}>Actions  </span>
            <div style={{ display: "inline" }}>
              <Button icon basic circular onClick={scrollToStart} size="tiny" >
                <Icon name="angle double up" />
              </Button>
              <Button icon basic circular onClick={scrollToEnd} size="tiny">
                <Icon name="angle double down" />
              </Button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="reader-header">
          <div className="reader-header-hint-field">Reader Data</div>
          <div className={filteredNodes.length > 17 ? 'reader-header-field extra-pad' : 'reader-header-field'}>
            <span style={{ height: '100%', marginTop: '4px' }}>Actions</span>
            <div style={{ float: 'right' }}>
              <Button icon basic circular onClick={scrollToStart} size="tiny" style={{ marginLeft: '10px' }}>
                <Icon name="angle double up" />
              </Button>
              <Button icon basic circular onClick={scrollToEnd} size="tiny">
                <Icon name="angle double down" />
              </Button>
            </div>
          </div>
        </div>
      );
    }
  };

  // Context for reordering and managing list items
  const contextValue = useMemo(
    () => ({
      reorderItem: handleMove,
      resetDroppedRow: () => setDroppedRow(null),
    }),
    [handleMove]
  );

  return (
    <>
      <div className="json-editor-container">
        {((editKey !== null && isEditEnabled) || editNewNode) && (
          <EditDialog node={nodes.find((node) => node.uniqueKey === editKey) || {}} onSave={handleUpdate} onCancel={handleCancel} />
        )}

        <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} size="mini">
          <Modal.Header>Delete Node</Modal.Header>
          <Modal.Content>
            <p>Are you sure you want to delete this node?</p>
          </Modal.Content>
          <Modal.Actions>
            <Button negative onClick={() => setIsDeleteModalOpen(false)}>No</Button>
            <Button positive onClick={confirmDelete}>Yes</Button>
          </Modal.Actions>
        </Modal>

        <SearchReplaceControls
          searchOptions={searchOptions}
          viewOptions={viewOptions}
          handleSearch={handleSearch}
          handleClearFilter={handleClearFilter}
          handleAddNew={() => setEditNewNode(true)}
          handleViewChange={setView}
          renderFileStats={renderFileStats}
        />
        <div className={filteredNodes.length < nodes.length ? 'searchStatsHeader align-field-right' : 'hide-field'}>
          <span className="filename-label">{`Search match count: ${filteredNodes.length}`}</span>
        </div>
        {renderHeader()}
      </div>
      <ListContext.Provider value={contextValue}>
        <div className="list-json-editor-container">
          <div ref={nodeListRef} style={{ height: '700px', overflowX: 'hidden', overflowY: 'scroll', border: '1px solid #ccc' }}>
            <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
              {rowVirtualizer.getVirtualItems().map((virtualItem, idx) => (
                <ListItem
                  key={`${filteredNodes[virtualItem.index].uniqueKey || 'item'}-${virtualItem.index}`} // Ensure key is always unique
                  node={filteredNodes[virtualItem.index]}
                  index={virtualItem.index}
                  virtualItem={virtualItem}
                  onMeasured={(index, height) => rowVirtualizer.resizeItem(index, height)}
                  editIndex={editKey}
                  view={view}
                  handleEdit={handleEdit}
                  handleMove={handleMove}
                  handleRowClick={handleRowClick}
                  handleDelete={handleDelete}
                  droppedRow={droppedRow}
                  nodeListRef={nodeListRef}
                  myListContext={ListContext}
                  searchQuery={searchQuery}  // pass down the search query

                />
              ))}
            </div>
          </div>
        </div>
      </ListContext.Provider>
    </>
  );
};

export default JsonStoryEditor;
